import React from 'react';
import { Alert, Button, Skeleton, Tag, Tooltip } from 'antd';
import {
  CarOutlined,
  CheckOutlined,
  FileDoneOutlined,
  MinusCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { SupplierOrderTransportDto } from '../../../dto/transport';
import { formatDate } from '../../../utils/dateUtils';
import './DeliveryTransportPicker.css';

/**
 * Etap 2 (transport przywozowy): "which truck is this receipt for?".
 *
 * One shared surface for both places that ask the question — the PZ generation
 * step (choose before creating) and the PZ document itself (see/change after
 * creating) — so the two can never drift into different wording or different
 * ideas of what "no transport" means.
 *
 * Reading order follows the yard, not the database: the vehicle registration is
 * the anchor (that is what the operator reads off the driver's paperwork), the
 * carrier and dates support it, and the internal route number stays a quiet
 * cross-reference. A transport that already produced a PZ for this supplier
 * order says so on the card, so "which truck did I already receive?" is
 * answered without leaving the screen.
 *
 * Selection is deliberately multi-value: one PZ may cover several trucks that
 * arrived together. Selecting nothing is a first-class outcome, not an error —
 * it reproduces the pre-Etap-2 behaviour of a receipt with no transport trace.
 */
interface DeliveryTransportPickerProps {
  transports: SupplierOrderTransportDto[];
  /** Selected transport ids. An empty array means the explicit "no transport" path. */
  value: string[];
  onChange?: (transportIds: string[]) => void;
  loading?: boolean;
  /** True when the transports could not be loaded — advisory, never blocking. */
  loadFailed?: boolean;
  onRetry?: () => void;
  /** Read-only rendering for a saved document: no checkboxes, selected rows only. */
  readOnly?: boolean;
  /** Hides the explicit "Bez transportu" row (e.g. when editing a saved PZ inline). */
  hideNoTransportOption?: boolean;
  /**
   * Opens a transport from a read-only row. Passed in (rather than routed here)
   * so this component stays free of react-router — which its jest consumers
   * cannot resolve, and which a picker has no business owning anyway.
   */
  onOpenTransport?: (transportId: string) => void;
  /**
   * Overrides the "nothing here" sentence. The default answers the question the
   * PZ flow asks; a different surface (the supplier order itself) is asking a
   * different one and must say so in its own words.
   */
  emptyMessage?: string;
  'data-testid'?: string;
}

/**
 * Which plate to show for a truck.
 *
 * The transport form writes `licensePlate1` (and optionally a second plate for
 * a trailer); `vehicleRegistration` is the older single-plate column that only
 * legacy rows — and older backend responses — carry. Reading just one of them
 * is what made every freshly created transport render "Brak nr rejestracyjnego".
 * Single home so the picker, the PZ document section and the supplier-order
 * page can never disagree about what a truck is called.
 */
export function resolveDeliveryTransportPlate(
  transport: Pick<SupplierOrderTransportDto, 'licensePlate1' | 'vehicleRegistration'>,
): string | null {
  // Each candidate is trimmed before it is considered, so a blank-but-present
  // licensePlate1 (a row saved with an empty plate field) still falls through to
  // the legacy column instead of winning with whitespace.
  return [transport.licensePlate1, transport.vehicleRegistration]
    .map((candidate) => (candidate || '').trim())
    .find((candidate) => candidate.length > 0) || null;
}

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'default',
  SCHEDULED: 'processing',
  LOADING: 'gold',
  IN_TRANSIT: 'blue',
  DELIVERED: 'success',
  CANCELLED: 'default',
  PLANNED: 'processing',
  DELAYED: 'error',
};

const DeliveryTransportPicker: React.FC<DeliveryTransportPickerProps> = ({
  transports,
  value,
  onChange,
  loading = false,
  loadFailed = false,
  onRetry,
  readOnly = false,
  hideNoTransportOption = false,
  onOpenTransport,
  emptyMessage,
  'data-testid': dataTestId = 'delivery-transport-picker',
}) => {
  const { t } = useTranslation(['transport', 'pz', 'common']);
  const selectedIds = new Set(value);

  const toggle = (transportId: string) => {
    if (readOnly || !onChange) {
      return;
    }

    onChange(
      selectedIds.has(transportId)
        ? value.filter((id) => id !== transportId)
        : [...value, transportId],
    );
  };

  if (loading) {
    return (
      <div className="delivery-transport-picker" data-testid={`${dataTestId}-loading`}>
        <Skeleton active paragraph={{ rows: 2 }} title={false} />
      </div>
    );
  }

  if (loadFailed) {
    return (
      <Alert
        className="delivery-transport-picker__alert"
        type="warning"
        showIcon
        data-testid={`${dataTestId}-error`}
        message={t('transport:supplierOrders.transportsLoadFailed')}
        action={onRetry ? (
          <Button size="small" icon={<ReloadOutlined />} onClick={onRetry}>
            {t('common:retry', { defaultValue: 'Ponów' })}
          </Button>
        ) : null}
      />
    );
  }

  const visibleTransports = readOnly
    ? transports.filter((transport) => selectedIds.has(transport.id))
    : transports;

  if (visibleTransports.length === 0) {
    // Never a blocker: a supplier order with no transport allocated to it is a
    // perfectly normal receipt, it just carries no truck trace.
    return (
      <p className="delivery-transport-picker__empty" data-testid={`${dataTestId}-empty`}>
        <CarOutlined aria-hidden="true" />
        <span>
          {emptyMessage || (readOnly
            ? t('pz:deliveryTransports.emptyOnDocument')
            : t('pz:deliveryTransports.emptyForSupplierOrder'))}
        </span>
      </p>
    );
  }

  return (
    <div className="delivery-transport-picker" data-testid={dataTestId}>
      {!readOnly && !hideNoTransportOption ? (
        <button
          type="button"
          role="checkbox"
          aria-checked={value.length === 0}
          data-testid={`${dataTestId}-none`}
          className={`delivery-transport-option delivery-transport-option--none${
            value.length === 0 ? ' is-selected' : ''
          }`}
          onClick={() => onChange?.([])}
        >
          <span className="delivery-transport-option__check" aria-hidden="true">
            {value.length === 0 ? <CheckOutlined /> : <MinusCircleOutlined />}
          </span>
          <span className="delivery-transport-option__body">
            <span className="delivery-transport-option__none-label">
              {t('pz:deliveryTransports.noTransport')}
            </span>
            <span className="delivery-transport-option__none-hint">
              {t('pz:deliveryTransports.noTransportHint')}
            </span>
          </span>
        </button>
      ) : null}

      {visibleTransports.map((transport) => {
        const isSelected = selectedIds.has(transport.id);
        const dates = [
          transport.loadingDate
            ? `${t('transport:fields.loadingDate')}: ${formatDate(transport.loadingDate)}`
            : null,
          transport.departureDate
            ? `${t('transport:departure')}: ${formatDate(transport.departureDate)}`
            : null,
        ].filter(Boolean) as string[];

        const plate = resolveDeliveryTransportPlate(transport);
        const identity = (
          <>
            <span className="delivery-transport-option__plate">
              {plate || t('pz:deliveryTransports.noPlate')}
            </span>
            <span className="delivery-transport-option__carrier">
              {transport.transportCompany || '—'}
            </span>
          </>
        );

        const details = (
          <>
            <span className="delivery-transport-option__meta">
              {transport.routeNumber && readOnly && onOpenTransport ? (
                <button
                  type="button"
                  className="delivery-transport-option__route delivery-transport-option__route-link"
                  onClick={() => onOpenTransport(transport.id)}
                >
                  {transport.routeNumber}
                </button>
              ) : transport.routeNumber ? (
                <span className="delivery-transport-option__route">{transport.routeNumber}</span>
              ) : null}
              {dates.map((date) => (
                <span key={date}>{date}</span>
              ))}
              <Tag
                bordered={false}
                color={STATUS_COLORS[transport.status] || 'default'}
                className="delivery-transport-option__status"
              >
                {t(`transport:status.${transport.status.toLowerCase()}`, {
                  defaultValue: t('common:unknownStatus'),
                })}
              </Tag>
            </span>
            {transport.pzNumbers.length > 0 ? (
              <span className="delivery-transport-option__received">
                <FileDoneOutlined aria-hidden="true" />
                {t('pz:deliveryTransports.alreadyReceived', {
                  numbers: transport.pzNumbers.join(', '),
                })}
              </span>
            ) : null}
          </>
        );

        if (readOnly) {
          return (
            <div
              key={transport.id}
              className="delivery-transport-option delivery-transport-option--readonly"
              data-testid={`${dataTestId}-row-${transport.id}`}
            >
              <span className="delivery-transport-option__check" aria-hidden="true">
                <CarOutlined />
              </span>
              <span className="delivery-transport-option__body">
                <span className="delivery-transport-option__identity">{identity}</span>
                {details}
              </span>
            </div>
          );
        }

        return (
          <button
            key={transport.id}
            type="button"
            role="checkbox"
            aria-checked={isSelected}
            data-testid={`${dataTestId}-option-${transport.id}`}
            className={`delivery-transport-option${isSelected ? ' is-selected' : ''}`}
            onClick={() => toggle(transport.id)}
          >
            <span className="delivery-transport-option__check" aria-hidden="true">
              {isSelected ? <CheckOutlined /> : null}
            </span>
            <span className="delivery-transport-option__body">
              <span className="delivery-transport-option__identity">
                {transport.pzNumbers.length > 0 ? (
                  <Tooltip title={t('pz:deliveryTransports.alreadyReceivedTooltip')}>
                    <span className="delivery-transport-option__identity-inner">{identity}</span>
                  </Tooltip>
                ) : (
                  identity
                )}
              </span>
              {details}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default DeliveryTransportPicker;
